// RestartOrderButton - TOPページ用の注文再開ボタン機能

interface SavedOrderData {
  productType?: string;
  beanType?: string;
  packageType?: string;
  selectedBean?: any;
  lotSize?: string;
  sugarConcentration?: string;
  needsOuterBox?: boolean;
  needsOuterBoxDesign?: boolean;
  currentProduct?: any;
  savedAt?: string;
  customerId?: string;
  timestamp?: number;
}

interface NormalizedOrderData {
  timestamp: number;
  customerId: string;
  step1?: { selectedTech: string };
  step2?: { selectedBean: string };
  step3?: { quantity: string };
  step4?: { sugarLevel: string };
  currentProduct?: any;
  productType?: string;
}

// 顧客情報の型定義
interface CustomerInfo {
  isLoggedIn: boolean;
  id: number | null;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  tags: string[];
}

// 現在の言語を取得（React側の実装と統一）
function getCurrentLanguage(): string {
  // window.wakoFlowLocale から現在の言語を取得
  const locale = (window as any).wakoFlowLocale;
  
  // URLベースでの言語判定（フォールバック）
  const pathname = window.location.pathname;
  const urlBasedLanguage = pathname.startsWith('/en/') ? 'en' : 'ja';
  
  // 日本以外は英語の判定ロジック（React側と同じ）
  if (locale === 'ja') {
    return 'ja';
  } else if (locale) {
    // 日本語以外のlocaleが設定されている場合は英語
    return 'en';
  } else {
    // localeが未設定の場合はURLから判定
    return urlBasedLanguage;
  }
}

// 顧客情報を取得
function getCustomerInfo(): CustomerInfo {
  return (window as any).wakoCustomerInfo || {
    isLoggedIn: false,
    id: null,
    email: null,
    firstName: null,
    lastName: null,
    tags: []
  };
}

// 顧客が注文可能かどうかを判定
function canCustomerOrder(): boolean {
  const customerInfo = getCustomerInfo();
  
  // ログインしていない場合は注文不可
  if (!customerInfo.isLoggedIn) {
    return false;
  }
  
  // 承認済みタグがない場合は注文不可
  if (!customerInfo.tags.includes('approved')) {
    return false;
  }
  
  return true;
}

// キャラクターWアイコンを画像として生成
function createCharacterWIcon(): HTMLElement {
  const iconContainer = document.createElement('div');
  
  const img = document.createElement('img');
  // theme.liquidで設定されたアセットURLを使用
  const assetUrls = (window as any).assetUrls;
  img.src = assetUrls?.iconCharacterW || '/assets/icon_character_w.svg';
  img.alt = 'キャラクターアイコン';
  
  iconContainer.appendChild(img);
  
  return iconContainer;
}

// 保存データの確認
function checkSavedOrderData(): boolean {
  try {
    // 顧客が注文可能かチェック
    if (!canCustomerOrder()) {
      return false;
    }
    
    const customerInfo = getCustomerInfo();
    
    // 複数のキーをチェック
    let savedData = localStorage.getItem('orderFlowData');
    let dataKey = 'orderFlowData';
    
    if (!savedData) {
      savedData = localStorage.getItem('wako-custom-order-progress');
      dataKey = 'wako-custom-order-progress';
    }
    
    if (!savedData) {
      return false;
    }

    const data: SavedOrderData = JSON.parse(savedData);
    
    // データ構造を統一化
    let normalizedData: NormalizedOrderData;
    if (dataKey === 'wako-custom-order-progress') {
      // wako-custom-order-progressの形式を変換
      normalizedData = {
        timestamp: data.savedAt ? new Date(data.savedAt).getTime() : Date.now(),
        customerId: data.customerId || '',
        step1: data.productType ? { selectedTech: data.productType } : undefined,
        step2: data.beanType ? { selectedBean: data.beanType } : undefined,
        step3: data.lotSize ? { quantity: data.lotSize } : undefined,
        step4: data.sugarConcentration ? { sugarLevel: data.sugarConcentration } : undefined,
        currentProduct: data.currentProduct
      };
    } else {
      normalizedData = data as NormalizedOrderData;
    }
    
    // データの有効性チェック
    if (!normalizedData.customerId || (!normalizedData.step1 && !normalizedData.productType)) {
      return false;
    }
    
    // 顧客IDの整合性チェック - 両方を文字列に変換して比較
    const savedCustomerId = String(normalizedData.customerId);
    const currentCustomerId = String(customerInfo.id);
    
    if (savedCustomerId !== currentCustomerId) {
      return false;
    }

    // 30日期限チェック
    const timestamp = normalizedData.timestamp || Date.now();
    const thirtyDaysAgo = Date.now() - (30 * 24 * 60 * 60 * 1000);
    
    if (timestamp < thirtyDaysAgo) {
      localStorage.removeItem(dataKey);
      return false;
    }

    return true;
  } catch (error) {
    console.error('RestartOrderButton: データチェックエラー', error);
    return false;
  }
}

// RestartOrderButtonを作成
function createRestartOrderButton(): HTMLElement {
  const container = document.createElement('div');
  container.className = 'restart-order-button';

  const btn = document.createElement('div');
  btn.className = 'restart-order-button__btn';

  const button = document.createElement('button');
  button.className = 'restart-order-button__content';
  button.type = 'button';

  const text = document.createElement('p');
  text.className = 'restart-order-button__text';
  text.textContent = getCurrentLanguage() === 'en' ? 'Restart previous order' : '前回の注文を再開する';

  const characterDiv = document.createElement('div');
  characterDiv.className = 'restart-order-button__character';
  characterDiv.appendChild(createCharacterWIcon());

  button.appendChild(text);
  btn.appendChild(button);
  container.appendChild(btn);
  container.appendChild(characterDiv);

  // クリックイベント
  button.addEventListener('click', () => {
    const targetUrl = '/pages/flow?restart=true';
    window.location.href = targetUrl;
  });

  return container;
}

// RestartOrderButton機能のメイン処理
export function restartOrderButton(): void {
  const mountPoint = document.getElementById('restart-order-button-mount');
  if (!mountPoint) {
    return;
  }

  // 保存データをチェック
  if (!checkSavedOrderData()) {
    return;
  }

  // ボタンを作成してマウント
  const button = createRestartOrderButton();
  mountPoint.appendChild(button);
}

 